import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FileText, Download, Plus, Loader2, AlertCircle, CheckCircle, Edit, Trash2, X, Calendar, Users, Search, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api, { invoicesApi } from '../lib/api';
import RevenueReport from './RevenueReport';

// Helper to format date for input
const formatDateForInput = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

// Helper to get today's date formatted
const getTodayFormatted = () => {
  return new Date().toISOString().split('T')[0];
};

const ManualInvoicing = () => {
  const { t, i18n } = useTranslation();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null); // For editing existing invoices
  
  // Billing customers state
  const [billingCustomers, setBillingCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerListSearch, setCustomerListSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    companyName: '',
    companyCIF: '',
    companyAddress: '',
    vehiclePlate: '',
    vehicleBrand: '',
    vehicleModel: '',
    paymentMethod: 'CASH',
    notes: '',
    // Date fields
    invoiceDate: getTodayFormatted(),
    serviceStartDate: getTodayFormatted(),
    serviceEndDate: getTodayFormatted()
  });

  // Line items / extras for the invoice
  const [lineItems, setLineItems] = useState([
    { id: `li-init-${Math.random().toString(36).slice(2, 10)}`, description: 'Servicio de parking', quantity: 1, unitPrice: '', total: 0 }
  ]);

  const [errors, setErrors] = useState({});

  // Load billing customers on mount
  useEffect(() => {
    loadBillingCustomers();
  }, []);

  const loadBillingCustomers = async (search = '') => {
    try {
      const response = await invoicesApi.getCustomers(search);
      setBillingCustomers(response.data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  // Select a customer and fill form
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customerName: customer.name || '',
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      companyName: customer.company_name || '',
      companyCIF: customer.cif_nif || '',
      companyAddress: customer.address || ''
    }));
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  // Save current form data as a new customer
  const saveAsNewCustomer = async () => {
    if (!formData.customerName) {
      toast.error('El nombre del cliente es obligatorio');
      return;
    }
    
    setSavingCustomer(true);
    try {
      const response = await invoicesApi.createCustomer({
        name: formData.customerName,
        email: formData.customerEmail,
        phone: formData.customerPhone,
        company_name: formData.companyName,
        cif_nif: formData.companyCIF,
        address: formData.companyAddress
      });
      
      if (response.data.success) {
        toast.success('Cliente guardado correctamente');
        setSelectedCustomer(response.data.customer);
        loadBillingCustomers(); // Refresh list
      }
    } catch (error) {
      toast.error('Error al guardar el cliente');
      console.error(error);
    } finally {
      setSavingCustomer(false);
    }
  };

  // Filter customers based on search
  const filteredCustomers = billingCustomers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.cif_nif?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const resetForm = () => {
    setFormData({
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      companyName: '',
      companyCIF: '',
      companyAddress: '',
      vehiclePlate: '',
      vehicleBrand: '',
      vehicleModel: '',
      paymentMethod: 'CASH',
      notes: '',
      invoiceDate: getTodayFormatted(),
      serviceStartDate: getTodayFormatted(),
      serviceEndDate: getTodayFormatted()
    });
    setLineItems([
      { id: `li-reset-${Math.random().toString(36).slice(2, 10)}`, description: 'Servicio de parking', quantity: 1, unitPrice: '', total: 0 }
    ]);
    setErrors({});
    setEditingInvoice(null);
    setSelectedCustomer(null);
    setCustomerSearch('');
  };

  // Add a new line item
  const addLineItem = () => {
    setLineItems([...lineItems, { id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, description: '', quantity: 1, unitPrice: '', total: 0 }]);
  };

  // Remove a line item
  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  // Update a line item
  const updateLineItem = (index, field, value) => {
    const newItems = [...lineItems];
    newItems[index][field] = value;
    
    // Calculate total for this line
    if (field === 'quantity' || field === 'unitPrice') {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const price = parseFloat(newItems[index].unitPrice) || 0;
      newItems[index].total = qty * price;
    }
    
    setLineItems(newItems);
  };

  // Calculate grand total
  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.total || 0), 0);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.companyName.trim()) newErrors.companyName = true;
    if (!formData.companyCIF.trim() || formData.companyCIF.length < 8) newErrors.companyCIF = true;
    if (!formData.companyAddress.trim()) newErrors.companyAddress = true;
    if (!formData.customerName.trim()) newErrors.customerName = true;
    if (!formData.customerEmail.trim() || !formData.customerEmail.includes('@')) newErrors.customerEmail = true;
    
    // Validate at least one line item with amount
    const hasValidLineItem = lineItems.some(item => item.total > 0);
    if (!hasValidLineItem) newErrors.lineItems = true;
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      toast.error('Completa todos los campos obligatorios', {
        icon: <AlertCircle className="w-5 h-5 text-red-500" />,
        duration: 4000
      });
      return false;
    }
    
    return true;
  };

  const handleCreateInvoice = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const totalCents = Math.round(calculateTotal() * 100);
      
      // Build line items data for backend
      const lineItemsData = lineItems
        .filter(item => item.total > 0)
        .map(item => ({
          description: item.description || 'Servicio',
          quantity: parseFloat(item.quantity) || 1,
          unit_price_cents: Math.round((parseFloat(item.unitPrice) || 0) * 100),
          total_cents: Math.round(item.total * 100)
        }));

      const reservationData = {
        customer_name: formData.customerName,
        customer_email: formData.customerEmail,
        customer_phone: formData.customerPhone || null,
        vehicle_plate: formData.vehiclePlate || 'MANUAL',
        vehicle_brand: formData.vehicleBrand || null,
        vehicle_model: formData.vehicleModel || null,
        total_cents: totalCents,
        total_days: lineItems.length,
        payment_method: formData.paymentMethod,
        notes: formData.notes || 'Factura manual',
        wants_invoice: true,
        billing_company_name: formData.companyName,
        billing_cif: formData.companyCIF,
        billing_address: formData.companyAddress,
        source_channel: 'MANUAL_INVOICE',
        // Date fields
        invoice_date: formData.invoiceDate,
        service_start_date: formData.serviceStartDate,
        service_end_date: formData.serviceEndDate,
        line_items: lineItemsData
      };

      let response;
      if (editingInvoice) {
        // Update existing invoice. Invoices in this app don't have a top-level
        // `id` field — backend accepts either `id` or `invoice_number` as the
        // path param, so we prefer invoice_number (always present and stable).
        const invoiceKey = editingInvoice.id || editingInvoice.invoice_number || editingInvoice.reservation_code;
        if (!invoiceKey) {
          toast.error('No se pudo identificar la factura a editar');
          return;
        }
        response = await api.put(`/invoices/${encodeURIComponent(invoiceKey)}`, reservationData);
        toast.success('Factura actualizada correctamente', {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />
        });
      } else {
        // Create new invoice
        response = await api.post('/invoices/manual', reservationData);
        toast.success('Factura creada correctamente', {
          icon: <CheckCircle className="w-5 h-5 text-green-500" />
        });
      }
      
      if (response.data.success || response.data.ok) {
        // Download the PDF
        const invoiceCode = response.data.reservation_code || response.data.invoice_number;
        if (invoiceCode) {
          window.open(invoicesApi.downloadPdf(invoiceCode), '_blank');
        }
        
        setShowCreateDialog(false);
        resetForm();
        loadRecentInvoices();
      }
    } catch (error) {
      console.error('Error creating/updating invoice:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar la factura');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const response = await api.get('/invoices/recent?limit=20');
      setRecentInvoices(response.data || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  // Edit an existing invoice
  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setFormData({
      customerName: invoice.customer_name || '',
      customerEmail: invoice.customer_email || '',
      customerPhone: invoice.customer_phone || '',
      companyName: invoice.billing_company_name || '',
      companyCIF: invoice.billing_cif || '',
      companyAddress: invoice.billing_address || '',
      vehiclePlate: invoice.vehicle_plate || '',
      vehicleBrand: invoice.vehicle_brand || '',
      vehicleModel: invoice.vehicle_model || '',
      paymentMethod: invoice.payment_method || 'CASH',
      notes: invoice.notes || '',
      // Load dates
      invoiceDate: formatDateForInput(invoice.invoice_date || invoice.created_at) || getTodayFormatted(),
      serviceStartDate: formatDateForInput(invoice.service_start_date || invoice.start_dt) || getTodayFormatted(),
      serviceEndDate: formatDateForInput(invoice.service_end_date || invoice.end_dt) || getTodayFormatted()
    });
    
    // Load line items if they exist, otherwise create from total
    if (invoice.line_items && invoice.line_items.length > 0) {
      setLineItems(invoice.line_items.map((item, idx) => ({
        id: item.id || `li-loaded-${idx}-${Math.random().toString(36).slice(2, 8)}`,
        description: item.description || 'Servicio',
        quantity: item.quantity || 1,
        unitPrice: (item.unit_price_cents / 100).toFixed(2),
        total: item.total_cents / 100
      })));
    } else {
      setLineItems([{
        id: `li-fallback-${Math.random().toString(36).slice(2, 10)}`,
        description: 'Servicio de parking',
        quantity: 1,
        unitPrice: (invoice.total_cents / 100).toFixed(2),
        total: invoice.total_cents / 100
      }]);
    }
    
    setShowCreateDialog(true);
  };

  React.useEffect(() => {
    loadRecentInvoices();
  }, []);

  // One-click "Facturar" from a saved customer: pre-fill the invoice form
  const startInvoiceForCustomer = (customer) => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      customerName: customer.name || '',
      customerEmail: customer.email || '',
      customerPhone: customer.phone || '',
      companyName: customer.company_name || '',
      companyCIF: customer.cif_nif || '',
      companyAddress: customer.address || ''
    }));
    // If this customer has a recent invoice, reuse its line items and payment method
    const lastInvoice = customer.cif_nif
      ? recentInvoices.find(inv => inv.billing_cif === customer.cif_nif)
      : null;
    if (lastInvoice) {
      if (lastInvoice.payment_method) {
        setFormData(prev => ({ ...prev, paymentMethod: lastInvoice.payment_method }));
      }
      if (lastInvoice.line_items?.length > 0) {
        setLineItems(lastInvoice.line_items.map((item, idx) => ({
          id: `li-cust-${idx}-${Math.random().toString(36).slice(2, 10)}`,
          description: item.description || '',
          quantity: item.quantity || 1,
          unitPrice: (item.unit_price_cents / 100).toFixed(2),
          total: item.total_cents / 100
        })));
      }
      toast.success('Factura pre-rellenada con la última factura del cliente');
    } else {
      toast.success('Datos del cliente cargados');
    }
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name || '');
    setShowCreateDialog(true);
  };

  const visibleSavedCustomers = billingCustomers.filter(c =>
    !customerListSearch ||
    c.name?.toLowerCase().includes(customerListSearch.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(customerListSearch.toLowerCase()) ||
    c.cif_nif?.toLowerCase().includes(customerListSearch.toLowerCase())
  );

  const inputClass = (field) => `input-gold ${errors[field] ? 'border-red-500 border-2' : ''}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Facturación Manual</h2>
          <p className="text-zinc-400">Crea y edita facturas con múltiples conceptos</p>
        </div>
        <Button 
          onClick={() => {
            resetForm();
            setShowCreateDialog(true);
          }}
          className="btn-primary"
          data-testid="new-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Factura
        </Button>
      </div>

      {/* Saved Customers - one-click invoicing */}
      {billingCustomers.length > 0 && (
        <div className="card-dark p-6" data-testid="saved-customers-card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Clientes Guardados ({billingCustomers.length})
            </h3>
            {billingCustomers.length > 4 && (
              <div className="relative sm:w-64">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={customerListSearch}
                  onChange={(e) => setCustomerListSearch(e.target.value)}
                  placeholder="Buscar cliente..."
                  className="input-gold pl-9 h-9 text-sm"
                  data-testid="saved-customers-search"
                />
              </div>
            )}
          </div>
          {visibleSavedCustomers.length === 0 ? (
            <p className="text-center py-4 text-zinc-500 text-sm">No se encontraron clientes</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {visibleSavedCustomers.map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid={`saved-customer-${customer.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium text-white truncate">{customer.name}</p>
                    <p className="text-sm text-zinc-400 truncate">
                      {customer.company_name && `${customer.company_name} · `}
                      {customer.cif_nif && `CIF: ${customer.cif_nif}`}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => startInvoiceForCustomer(customer)}
                    className="btn-primary ml-3 flex-shrink-0"
                    data-testid={`invoice-customer-btn-${customer.id}`}
                  >
                    <FileText className="w-4 h-4 mr-1.5" />
                    Facturar
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Revenue Report */}
      <RevenueReport />

      {/* Recent Invoices */}
      <div className="card-dark p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Facturas Recientes</h3>
        
        {loadingInvoices ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : recentInvoices.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            No hay facturas recientes
          </div>
        ) : (
          <div className="space-y-3">
            {recentInvoices.map((invoice) => (
              <div 
                key={invoice.id}
                className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                data-testid={`invoice-row-${invoice.invoice_number}`}
              >
                <div className="flex items-center gap-4">
                  <FileText className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-medium text-white">{invoice.invoice_number}</p>
                    <p className="text-sm text-zinc-400">
                      {invoice.billing_company_name || invoice.customer_name} - {(invoice.total_cents / 100).toFixed(2)}€
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-500">
                    {new Date(invoice.invoice_date || invoice.created_at).toLocaleDateString('es-ES')}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditInvoice(invoice)}
                    className="text-zinc-400 hover:text-primary"
                    data-testid={`edit-invoice-${invoice.invoice_number}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(invoicesApi.downloadPdf(invoice.reservation_code), '_blank')}
                    className="text-zinc-400 hover:text-primary"
                    data-testid={`download-invoice-${invoice.invoice_number}`}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Invoice Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        if (!open) resetForm();
        setShowCreateDialog(open);
      }}>
        <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              {editingInvoice ? `Editar Factura ${editingInvoice.invoice_number}` : 'Nueva Factura Manual'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Alert for required fields */}
            <div className="p-3 bg-primary/10 border border-primary/30 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-sm text-primary">
                Los campos marcados con <span className="text-red-500">*</span> son obligatorios
              </span>
            </div>

            {/* Invoice Dates Section */}
            <div className="space-y-4">
              <h4 className="text-white font-semibold border-b border-white/10 pb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                Fechas de la Factura
              </h4>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-white">
                    Fecha de emisión <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="date"
                    value={formData.invoiceDate}
                    onChange={(e) => setFormData({...formData, invoiceDate: e.target.value})}
                    className="input-gold"
                    data-testid="invoice-date"
                  />
                </div>
                <div>
                  <Label className="text-white">
                    Fecha inicio servicio
                  </Label>
                  <Input
                    type="date"
                    value={formData.serviceStartDate}
                    onChange={(e) => setFormData({...formData, serviceStartDate: e.target.value})}
                    className="input-gold"
                    data-testid="service-start-date"
                  />
                </div>
                <div>
                  <Label className="text-white">
                    Fecha fin servicio
                  </Label>
                  <Input
                    type="date"
                    value={formData.serviceEndDate}
                    onChange={(e) => setFormData({...formData, serviceEndDate: e.target.value})}
                    className="input-gold"
                    data-testid="service-end-date"
                  />
                </div>
              </div>
            </div>

            {/* Billing Data Section */}
            <div className="space-y-4">
              <h4 className="text-white font-semibold border-b border-white/10 pb-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                Datos de Facturación
              </h4>
              
              {/* Customer Search/Select */}
              <div className="relative">
                <Label className="text-white flex items-center gap-2">
                  <Search className="w-4 h-4" />
                  Buscar cliente guardado
                </Label>
                <div className="relative">
                  <Input
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerDropdown(true);
                      if (e.target.value === '') {
                        setSelectedCustomer(null);
                      }
                    }}
                    onFocus={() => setShowCustomerDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                    placeholder="Escribe para buscar cliente..."
                    className="input-gold"
                    data-testid="customer-search"
                  />
                  {selectedCustomer && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                      <CheckCircle className="w-4 h-4" />
                    </span>
                  )}
                </div>
                
                {/* Dropdown with customers */}
                {showCustomerDropdown && customerSearch && filteredCustomers.length > 0 && (
                  <div className="absolute z-[100] w-full mt-1 bg-zinc-800 border border-zinc-600 rounded-lg shadow-2xl max-h-48 overflow-y-auto">
                    {filteredCustomers.map(customer => (
                      <button
                        key={customer.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectCustomer(customer);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-primary/20 transition-colors border-b border-zinc-700 last:border-0"
                      >
                        <p className="text-white font-medium">{customer.name}</p>
                        <p className="text-sm text-zinc-400">
                          {customer.company_name && `${customer.company_name} · `}
                          {customer.cif_nif && `CIF: ${customer.cif_nif}`}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
                
                {showCustomerDropdown && customerSearch && filteredCustomers.length === 0 && (
                  <div className="absolute z-[100] w-full mt-1 bg-zinc-800 border border-zinc-600 rounded-lg shadow-2xl p-4 text-center text-zinc-400">
                    No se encontraron clientes
                  </div>
                )}
              </div>

              {/* Save as new customer button */}
              {formData.customerName && !selectedCustomer && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={saveAsNewCustomer}
                  disabled={savingCustomer}
                  className="text-primary border-primary/50 hover:bg-primary/10"
                >
                  {savingCustomer ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar como nuevo cliente
                </Button>
              )}
              
              <div>
                <Label className="text-white">
                  Nombre de la empresa <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.companyName}
                  onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                  placeholder="Ej: Mi Empresa S.L."
                  className={inputClass('companyName')}
                  data-testid="invoice-company-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">
                    CIF/NIF <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.companyCIF}
                    onChange={(e) => setFormData({...formData, companyCIF: e.target.value.toUpperCase()})}
                    placeholder="B12345678"
                    className={inputClass('companyCIF')}
                    data-testid="invoice-company-cif"
                  />
                </div>
                <div>
                  <Label className="text-white">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
                    placeholder="email@empresa.com"
                    className={inputClass('customerEmail')}
                    data-testid="invoice-email"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white">
                  Dirección fiscal <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={formData.companyAddress}
                  onChange={(e) => setFormData({...formData, companyAddress: e.target.value})}
                  placeholder="Calle, número, código postal, ciudad..."
                  className={inputClass('companyAddress')}
                  rows={2}
                  data-testid="invoice-address"
                />
              </div>
            </div>

            {/* Customer Data Section */}
            <div className="space-y-4">
              <h4 className="text-white font-semibold border-b border-white/10 pb-2">
                Datos del Cliente
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">
                    Nombre completo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.customerName}
                    onChange={(e) => setFormData({...formData, customerName: e.target.value})}
                    placeholder="Nombre y apellidos"
                    className={inputClass('customerName')}
                    data-testid="invoice-customer-name"
                  />
                </div>
                <div>
                  <Label className="text-white">Teléfono</Label>
                  <Input
                    value={formData.customerPhone}
                    onChange={(e) => setFormData({...formData, customerPhone: e.target.value})}
                    placeholder="+34 600 000 000"
                    className="input-gold"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Data Section */}
            <div className="space-y-4">
              <h4 className="text-white font-semibold border-b border-white/10 pb-2">
                Datos del Vehículo (opcional)
              </h4>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-white">Matrícula</Label>
                  <Input
                    value={formData.vehiclePlate}
                    onChange={(e) => setFormData({...formData, vehiclePlate: e.target.value.toUpperCase()})}
                    placeholder="1234ABC"
                    className="input-gold"
                  />
                </div>
                <div>
                  <Label className="text-white">Marca</Label>
                  <Input
                    value={formData.vehicleBrand}
                    onChange={(e) => setFormData({...formData, vehicleBrand: e.target.value})}
                    placeholder="Seat"
                    className="input-gold"
                  />
                </div>
                <div>
                  <Label className="text-white">Modelo</Label>
                  <Input
                    value={formData.vehicleModel}
                    onChange={(e) => setFormData({...formData, vehicleModel: e.target.value})}
                    placeholder="León"
                    className="input-gold"
                  />
                </div>
              </div>
            </div>

            {/* Line Items / Extras Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between border-b border-white/10 pb-2">
                <h4 className="text-white font-semibold">
                  Conceptos de Factura <span className="text-red-500">*</span>
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={addLineItem}
                  className="text-primary hover:text-primary/80"
                  data-testid="add-line-item-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Añadir Concepto
                </Button>
              </div>
              
              {errors.lineItems && (
                <p className="text-red-500 text-sm">Añade al menos un concepto con importe</p>
              )}

              <div className="space-y-3">
                {/* Header */}
                <div className="grid grid-cols-12 gap-2 text-sm text-zinc-400 font-medium">
                  <div className="col-span-5">Descripción</div>
                  <div className="col-span-2 text-center">Cantidad</div>
                  <div className="col-span-2 text-center">Precio Unit.</div>
                  <div className="col-span-2 text-right">Total</div>
                  <div className="col-span-1"></div>
                </div>

                {/* Line items */}
                {lineItems.map((item, index) => (
                  <div key={item.id || `li-${index}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-item-${index}`}>
                    <div className="col-span-5">
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        placeholder="Descripción del servicio"
                        className="input-gold text-sm"
                        data-testid={`line-item-desc-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                        className="input-gold text-sm text-center"
                        data-testid={`line-item-qty-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.unitPrice}
                        onChange={(e) => updateLineItem(index, 'unitPrice', e.target.value)}
                        placeholder="0.00"
                        className="input-gold text-sm text-center"
                        data-testid={`line-item-price-${index}`}
                      />
                    </div>
                    <div className="col-span-2 text-right">
                      <span className="text-white font-medium">
                        {item.total.toFixed(2)}€
                      </span>
                    </div>
                    <div className="col-span-1 text-center">
                      {lineItems.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          className="text-red-500 hover:text-red-400 p-1 h-auto"
                          data-testid={`remove-line-item-${index}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Total */}
                <div className="grid grid-cols-12 gap-2 pt-3 border-t border-white/10">
                  <div className="col-span-9 text-right text-white font-semibold">
                    TOTAL:
                  </div>
                  <div className="col-span-2 text-right">
                    <span className="text-primary text-xl font-bold">
                      {calculateTotal().toFixed(2)}€
                    </span>
                  </div>
                  <div className="col-span-1"></div>
                </div>
              </div>
            </div>

            {/* Payment Method & Notes */}
            <div className="space-y-4">
              <h4 className="text-white font-semibold border-b border-white/10 pb-2">
                Método de Pago
              </h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Método de pago</Label>
                  <Select 
                    value={formData.paymentMethod} 
                    onValueChange={(value) => setFormData({...formData, paymentMethod: value})}
                  >
                    <SelectTrigger className="input-gold">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      <SelectItem value="CASH">Efectivo</SelectItem>
                      <SelectItem value="TPV">Tarjeta/Bizum</SelectItem>
                      <SelectItem value="STRIPE">Pago Online</SelectItem>
                      <SelectItem value="TRANSFER">Transferencia</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-white">Notas</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Observaciones adicionales..."
                    className="input-gold"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateInvoice}
                disabled={loading}
                className="btn-primary"
                data-testid="save-invoice-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingInvoice ? 'Actualizando...' : 'Creando...'}
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    {editingInvoice ? 'Actualizar Factura' : 'Crear y Descargar Factura'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManualInvoicing;
